import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Calendar, Clock, Check, Loader2, ArrowLeft } from "lucide-react";

function slotTimeLabel(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
}

export default function AdvisorBook() {
  const [params] = useSearchParams();
  const initialProgram = params.get("program") || "";
  const initialEmail = params.get("email") || "";
  const initialName = params.get("name") || "";

  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState("pick"); // pick | form | done
  const [form, setForm] = useState({
    name: initialName, email: initialEmail, phone: "",
    program_slug: initialProgram, message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/advisor/slots").then((r) => {
      setDays(r.data.days || []);
      if (r.data.days?.length) setSelectedDate(r.data.days[0].date);
    }).finally(() => setLoadingSlots(false));
  }, []);

  const currentDay = useMemo(() => days.find((d) => d.date === selectedDate), [days, selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/advisor/book", {
        ...form,
        program_slug: form.program_slug || null,
        slot_start: selectedSlot,
      });
      setConfirmation(data);
      setStep("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "done" && confirmation) {
    return (
      <div data-testid="advisor-booked" className="max-w-[900px] mx-auto px-6 md:px-10 py-20">
        <div className="w-14 h-14 rounded-full bg-novya-accent flex items-center justify-center">
          <Check size={28} className="text-white" />
        </div>
        <h1 className="mt-8 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-novya-ink">
          Booked<span className="text-novya-accent">.</span>
        </h1>
        <p className="mt-6 text-novya-muted max-w-xl leading-relaxed">
          Your 15-minute advisor call is confirmed for <span className="text-novya-ink font-medium">{confirmation.slot_display}</span>. You&apos;ll receive a confirmation email and WhatsApp — we&apos;ll share the meeting link 30 minutes before start.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/programs" data-testid="advisor-done-programs-btn" className="px-8 py-4 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors">
            Explore programs
          </Link>
          <Link to="/" data-testid="advisor-done-home-btn" className="px-8 py-4 border border-novya-ink text-novya-ink font-medium hover:bg-novya-ink hover:text-white transition-colors">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="advisor-book-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">Advisor call · 15 minutes · free</div>
      <h1 className="mt-4 font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] text-novya-ink max-w-4xl">
        Talk to a Novya advisor.
      </h1>
      <p className="mt-6 text-base md:text-lg text-novya-muted max-w-2xl leading-relaxed">
        Pick a 15-minute slot below. We&apos;ll walk you through curriculum, mentors, cohort dates, and answer anything before you enrol.
      </p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Left: date + slots */}
        <div className="md:col-span-7">
          {loadingSlots ? (
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-novya-muted">Loading slots…</div>
          ) : days.length === 0 ? (
            <div className="text-novya-muted">No open slots in the next two weeks. Try again soon.</div>
          ) : (
            <>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-4 flex items-center gap-2">
                <Calendar size={12} /> Pick a date
              </div>
              <div className="flex flex-wrap gap-2" data-testid="date-strip">
                {days.map((d) => (
                  <button
                    key={d.date}
                    data-testid={`date-${d.date}`}
                    onClick={() => { setSelectedDate(d.date); setSelectedSlot(null); }}
                    className={`px-4 py-3 border transition-colors ${
                      selectedDate === d.date
                        ? "bg-novya-ink text-white border-novya-ink"
                        : "border-novya-border text-novya-ink hover:border-novya-ink"
                    }`}
                  >
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">{d.weekday}</div>
                    <div className="mt-1 font-display font-bold text-lg">{d.label.split(" ")[1]}</div>
                  </button>
                ))}
              </div>

              <div className="mt-10 font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-4 flex items-center gap-2">
                <Clock size={12} /> Pick a time (IST)
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="slots-grid">
                {(currentDay?.slots || []).map((s) => (
                  <button
                    key={s}
                    data-testid={`slot-${s}`}
                    onClick={() => { setSelectedSlot(s); setStep("form"); }}
                    className={`py-3 border font-mono text-sm transition-colors ${
                      selectedSlot === s
                        ? "bg-novya-accent text-white border-novya-accent"
                        : "border-novya-border text-novya-ink hover:border-novya-ink"
                    }`}
                  >
                    {slotTimeLabel(s)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: form */}
        <div className="md:col-span-5">
          {step === "pick" ? (
            <div className="border border-dashed border-novya-border p-10 text-novya-muted h-full flex items-center justify-center text-center">
              Pick a date and time to continue.
            </div>
          ) : (
            <form onSubmit={submit} className="border border-novya-border p-8 bg-white" data-testid="booking-form">
              <button
                type="button"
                onClick={() => { setStep("pick"); setSelectedSlot(null); }}
                data-testid="booking-back-btn"
                className="inline-flex items-center gap-1 font-mono text-xs tracking-[0.2em] uppercase text-novya-muted mb-6 hover:text-novya-ink"
              >
                <ArrowLeft size={12} /> Change slot
              </button>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-accent">Selected slot</div>
              <div className="mt-2 font-display font-bold text-2xl text-novya-ink" data-testid="selected-slot-display">
                {selectedSlot && new Date(selectedSlot).toLocaleString("en-IN", {
                  weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
                })} IST
              </div>

              <div className="mt-6 space-y-5">
                <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="advisor-name-input" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="advisor-email-input" />
                <Field label="WhatsApp number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="advisor-phone-input" placeholder="+91…" />
                <Field label="What would you like to discuss? (optional)" value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="advisor-message-input" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="advisor-confirm-btn"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 py-3.5 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors disabled:opacity-50"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Booking…</> : "Confirm booking"}
              </button>
              <p className="mt-3 text-[11px] font-mono tracking-wide text-novya-muted text-center">
                Free · No credit card · 15 minutes
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full border-b border-novya-ink bg-transparent py-3 outline-none focus:border-novya-accent"
      />
    </label>
  );
}
